import type { FileWithId, TChatHistory } from 'src/types';

import { useMemo, useState, useReducer, useCallback, createContext } from 'react';

import { chatHistoryReducer, initialChatHistoryState } from '../reducers/chatHistoryReducer';

import type { ChatActionPayload } from '../reducers/chatHistoryReducer';

type TChatHistoryContext = {
  history: TChatHistory;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: ChatActionPayload<'SET_ERROR'>) => void;
  setUserMessage: (values: ChatActionPayload<'SET_USER_MESSAGE'>) => void;
  setLoadingMessage: VoidFunction;
  updateAssistantResponse: (values: ChatActionPayload<'UPDATE_ASSISTANT_RESPONSE'>) => void;
  updateFeedback: (values: ChatActionPayload<'UPDATE_FEEDBACK'>) => void;
  setRunId: (values: ChatActionPayload<'SET_RUN_ID'>) => void;
  setIntentId: (values: ChatActionPayload<'SET_INTENT_ID'>) => void;
  setDocumentId: (values: ChatActionPayload<'SET_DOCUMENT_ID'>) => void;
  allFiles: FileWithId[];
  setAllFiles: (files: FileWithId[]) => void;
  clearHistory: VoidFunction;
  removeFileFromContext: (id: string) => void;
  currentFileFormat: string | undefined;
  setCurrentFileFormat: (format: string | undefined) => void;
};

type TChatHistoryContextProvider = {
  children: React.ReactNode;
};

const ChatHistoryContext = createContext<TChatHistoryContext | undefined>(undefined);

function ChatHistoryContextProvider({ children }: TChatHistoryContextProvider) {
  const [history, dispatch] = useReducer(chatHistoryReducer, initialChatHistoryState);
  const [currentFileFormat, setCurrentFileFormat] = useState<string | undefined>(undefined);
  const [allFiles, setAllFiles] = useState<FileWithId[]>([]);

  const setError = useCallback((payload: ChatActionPayload<'SET_ERROR'>) => {
    dispatch({ type: 'SET_ERROR', ...payload });
  }, []);

  const setUserMessage = useCallback((payload: ChatActionPayload<'SET_USER_MESSAGE'>) => {
    dispatch({ type: 'SET_USER_MESSAGE', ...payload });
  }, []);

  const setLoadingMessage = useCallback(() => {
    dispatch({ type: 'SET_LOADING_MESSAGE', message: '' });
  }, []);

  const updateAssistantResponse = useCallback(
    (payload: ChatActionPayload<'UPDATE_ASSISTANT_RESPONSE'>) => {
      dispatch({ type: 'UPDATE_ASSISTANT_RESPONSE', ...payload });
    },
    []
  );

  const updateFeedback = useCallback((payload: ChatActionPayload<'UPDATE_FEEDBACK'>) => {
    dispatch({ type: 'UPDATE_FEEDBACK', ...payload });
  }, []);

  const setRunId = useCallback((payload: ChatActionPayload<'SET_RUN_ID'>) => {
    dispatch({ type: 'SET_RUN_ID', ...payload });
  }, []);

  const setIntentId = useCallback((payload: ChatActionPayload<'SET_INTENT_ID'>) => {
    dispatch({ type: 'SET_INTENT_ID', ...payload });
  }, []);

  const setDocumentId = useCallback((payload: ChatActionPayload<'SET_DOCUMENT_ID'>) => {
    dispatch({ type: 'SET_DOCUMENT_ID', ...payload });
  }, []);

  const clearHistory = useCallback(() => {
    setAllFiles([]);
    setCurrentFileFormat(undefined);
    dispatch({ type: 'CLEAR_HISTORY' });
  }, []);

  const removeFileFromContext = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_FILE_FROM_CONTEXT', id });
  }, []);

  const setIsLoading = useCallback((value: boolean) => {
    dispatch({ type: 'SET_LOADING', isLoading: value });
  }, []);

  const values = useMemo(
    () => ({
      history,
      setIsLoading,
      setError,
      setUserMessage,
      setLoadingMessage,
      updateAssistantResponse,
      updateFeedback,
      setRunId,
      setIntentId,
      setDocumentId,
      allFiles,
      setAllFiles,
      clearHistory,
      removeFileFromContext,
      currentFileFormat,
      setCurrentFileFormat,
    }),
    [
      history,
      setIsLoading,
      setError,
      setUserMessage,
      setLoadingMessage,
      updateAssistantResponse,
      updateFeedback,
      setRunId,
      setIntentId,
      setDocumentId,
      allFiles,
      setAllFiles,
      clearHistory,
      removeFileFromContext,
      currentFileFormat,
      setCurrentFileFormat,
    ]
  );

  return <ChatHistoryContext.Provider value={values}>{children}</ChatHistoryContext.Provider>;
}
export { ChatHistoryContext, ChatHistoryContextProvider };
